// Interview session storage — the single owner of the snapshot that authorizes
// the interview and the photogrammetry upload. Key: `interview:${slug}:resume`.
//
// Two-store model:
//   - sessionStorage is the primary store: fast, tab-scoped, and the natural
//     lifetime for an in-progress interview.
//   - localStorage is a durable mirror: it SURVIVES A CLOSED TAB. The subject is
//     invited to close the page during the ~1hr photogrammetry wait, so the
//     resume credential must outlive the tab to let them return without
//     re-clicking the email link.
//
// The mirror is only safe because the credential is erased the instant the
// server confirms it is dead — InterviewLanding clears on the completion /
// terminal / session-expired stages. Without that clear, a spent credential
// would linger in localStorage and route returning subjects to a dead-end.
//
// The resume_token in this snapshot is the ONLY authorization for the session-
// token edge calls (interview-session-status, r2-presign-photos, etc.), so both
// InterviewLanding (read/write) and PhotoUpload (read resume_token) go through
// these helpers rather than touching storage directly. Every store access is
// independently try/catch-wrapped so private-mode / disabled / full storage
// degrades to "no session" rather than throwing into the flow — and a failure in
// one store never prevents the other from being used.

import type { ConsentStatementsPayload } from "@/lib/interviewTypes";

export type StoredSession = {
  resume_token: string;
  interview_id: string;
  tribute_slug: string;
  session_expires_at: string;
  interview_status: string;
  // tributes.status snapshot — the photo hand-off signal ("awaiting_photos" =>
  // hand-off). Refreshed on every consume/resume so re-entry routes off the LIVE
  // value. null when unknown (older stored shape / read error) -> no hand-off.
  tribute_status?: string | null;
  answered_question_ids: string[];
  skipped_question_ids: string[];
  consent_statements?: ConsentStatementsPayload;
};

const storageKey = (slug: string) => `interview:${slug}:resume`;

// Parse + validate a raw stored string into a StoredSession, or null when it is
// malformed or expired. Pure — no storage side effects; the caller decides which
// store to evict. Validation is UNCHANGED from the pre-mirror single-store
// reader: require resume_token AND interview_id; expired when session_expires_at
// has passed; same field defaults for older stored shapes.
const parseStored = (raw: string): StoredSession | null => {
  try {
    const parsed = JSON.parse(raw) as Partial<StoredSession>;
    if (!parsed.resume_token || !parsed.interview_id) return null;
    if (parsed.session_expires_at && new Date(parsed.session_expires_at).getTime() <= Date.now()) {
      return null;
    }
    return {
      resume_token: parsed.resume_token,
      interview_id: parsed.interview_id,
      tribute_slug: parsed.tribute_slug ?? "",
      session_expires_at: parsed.session_expires_at ?? "",
      interview_status: parsed.interview_status ?? "",
      tribute_status: parsed.tribute_status ?? null,
      answered_question_ids: parsed.answered_question_ids ?? [],
      skipped_question_ids: parsed.skipped_question_ids ?? [],
      consent_statements: parsed.consent_statements,
    };
  } catch {
    return null;
  }
};

// Read one store: return a valid session, or null. If the entry is present but
// unusable (malformed / expired), evict it FROM THIS STORE ONLY; an absent entry
// is left untouched.
const readOneStore = (store: Storage, key: string): StoredSession | null => {
  try {
    const raw = store.getItem(key);
    if (!raw) return null;
    const session = parseStored(raw);
    if (session) return session;
    clearOneStore(store, key);
    return null;
  } catch {
    return null;
  }
};

const writeOneStore = (store: Storage, key: string, session: StoredSession): void => {
  try {
    store.setItem(key, JSON.stringify(session));
  } catch {
    // Store unavailable (private mode / disabled / quota) — non-fatal.
  }
};

const clearOneStore = (store: Storage, key: string): void => {
  try {
    store.removeItem(key);
  } catch {
    // Store unavailable (private mode / disabled) — non-fatal.
  }
};

// Resolve a storage object, or null if it can't be used. Merely ACCESSING
// window.sessionStorage / window.localStorage can throw a SecurityError (storage
// disabled, cookies blocked, sandboxed iframe), so resolution is guarded
// separately from use — the readOneStore/writeOneStore/clearOneStore guards only
// cover the getItem/setItem/removeItem call, not the property access itself.
const getStore = (kind: "session" | "local"): Storage | null => {
  try {
    if (typeof window === "undefined") return null;
    return kind === "session" ? window.sessionStorage : window.localStorage;
  } catch {
    return null;
  }
};

export function readStoredSession(slug: string): StoredSession | null {
  const key = storageKey(slug);

  // 1. Primary store. A valid session here wins immediately.
  const sessionStore = getStore("session");
  if (sessionStore) {
    const fromSession = readOneStore(sessionStore, key);
    if (fromSession) return fromSession;
  }

  // 2. Durable mirror (survives tab close). On a hit, rehydrate sessionStorage so
  //    the rest of the tab's reads stay on the fast path.
  const localStore = getStore("local");
  if (localStore) {
    const fromLocal = readOneStore(localStore, key);
    if (fromLocal) {
      if (sessionStore) writeOneStore(sessionStore, key, fromLocal);
      return fromLocal;
    }
  }

  return null;
}

export function writeStoredSession(slug: string, session: StoredSession): void {
  const key = storageKey(slug);
  const sessionStore = getStore("session");
  if (sessionStore) writeOneStore(sessionStore, key, session);
  const localStore = getStore("local");
  if (localStore) writeOneStore(localStore, key, session);
}

export function clearStoredSession(slug: string): void {
  const key = storageKey(slug);
  const sessionStore = getStore("session");
  if (sessionStore) clearOneStore(sessionStore, key);
  const localStore = getStore("local");
  if (localStore) clearOneStore(localStore, key);
}
